import { useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function RemindMeButton({ productId, productTitle, className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState();
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const onTrigger = (e) => {
    if (!user) {
      e.preventDefault();
      toast.error("Please sign in to set a reminder");
      navigate("/login");
      return;
    }
    setOpen(true);
  };

  const submit = async () => {
    if (!date) return toast.error("Please pick a date");
    setLoading(true);
    try {
      await api.post("/reminders", {
        product_id: productId,
        remind_date: format(date, "yyyy-MM-dd"),
        note,
      });
      toast.success(`We'll remind you on ${format(date, "PPP")}`);
      setOpen(false);
      setDate(undefined);
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not set reminder");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" className={className} onClick={onTrigger} data-testid="remind-me-btn">
        <Bell className="w-4 h-4 mr-1.5" /> Remind me
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Set a reminder</DialogTitle>
          <DialogDescription>
            We'll send you a WhatsApp/email nudge on your chosen date about <strong>{productTitle}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium block mb-1.5">Remind me on</label>
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-sm hover:border-teal" data-testid="remind-date-trigger">
                  {date ? format(date, "PPP") : "Pick a date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1.5">Note (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anniversary trip, plan with family etc."
              rows={2}
              data-testid="remind-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="remind-save">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
